import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, InputBase, makeStyles, Tooltip } from '@material-ui/core';
import { BrokerFavoriteListing } from 'models';
import { images } from 'assets/images';
import { useIntl } from 'react-intl';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import { COLOR } from 'config/constants';

const useStyles = makeStyles({
  filterButton: {
    backgroundColor: 'white',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRight: '2px solid #F5F5F5',
    width: 60,
    cursor: 'pointer',
    height: 50,
    '@media (max-width: 400px)': {
      width: 50,
    },
  },
  filterContainer: {
    display: 'flex',
    '@media (max-width: 650px)': {
      border: '2px solid #F5F5F5',
    },
  },
  filterButtonIcon: {
    width: 30,
    height: 30,
  },
  selectedFilterButton: {
    backgroundColor: '#00BDF2',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRight: '2px solid #F5F5F5',
    width: 60,
    cursor: 'pointer',
    height: 50,
    '@media (max-width: 400px)': {
      width: 50,
    },
  },
  searchBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    backgroundColor: '#f5f5f5',
    minWidth: 300,
    height: 50,
    borderRadius: 8,
    border: `1px solid ${COLOR.LIGHT_GREY}`,
    '@media (max-width: 650px)': {
      flexDirection: 'column',
      justifyContent: 'space-evenly',
      alignItems: 'center',
      backgroundColor: 'white',
      height: 'auto',
      border: 'none',
    },
  },
  searchInput: {
    paddingLeft: 10,
    backgroundColor: '#f5f5f5',
    height: 50,
    width: '100%',
    borderRadius: 8,
    '@media (max-width: 650px)': {
      height: 50,
      marginBottom: 10,
      border: `1px solid ${COLOR.GREY}`,
    },
  },
  searchButton: {
    color: 'white',
    borderRadius: '0 5px 5px 0',
    height: 50,
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
    '@media (max-width: 650px)': {
      display: 'none',
    },
  },
  clearSearchIcon: {
    color: 'red',
    marginRight: 10,
    cursor: 'pointer',
    width: 20,
    height: 20,
  },
  clearSearchButton: {
    color: 'white',
    backgroundColor: '#FF2E2E',
    marginBottom: 10,
    '&:hover': {
      backgroundColor: '#D10000',
    },
  },
});

const filters = [
  { name: 'drone', image_src: images.drone_dark, image_src_selected: images.drone_white },
  {
    name: 'virtual_staging',
    image_src: images.virtualStagingDark,
    image_src_selected: images.virtual_staging_white,
  },
  {
    name: 'floorplan',
    image_src: images.floorplan_dark,
    image_src_selected: images.floorplan_white,
  },
  {
    name: 'blue_hour',
    image_src: images.blue_hour_dark,
    image_src_selected: images.blue_hour_white,
  },
  { name: 'video', image_src: images.video_dark, image_src_selected: images.video_white },
  {
    name: 'matterport',
    image_src: images.matterport_svg,
    image_src_selected: images.matterport_svg,
  },
];

interface Props {
  selected_filters: any;
  setSelectedFilters: (selected_filters: any) => void;
  setSearchResults: (searchResults: BrokerFavoriteListing[]) => void;
  handleListingSearch: (search_input: string) => void;
  style?: any;
}

function SearchBar(props: Props) {
  const { selected_filters, setSelectedFilters, setSearchResults, handleListingSearch, style } =
    props;
  const styles = useStyles();
  const intl = useIntl();
  const [search_input, setSearchInput] = useState<string>('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const fmtSearchListing = intl.formatMessage({ id: 'searchListing' });
  const fmtClearSearch = intl.formatMessage({ id: 'portal.clearSearch' });

  const primary_color = style?.primary_color || COLOR.LIGHT_BLUE;
  const secondary_color = style?.secondary_color || COLOR.LIGHT_BLUE;
  useEffect(() => {
    const handleWindowResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  });

  function loadClearSearchButton() {
    if (search_input !== '' && windowWidth > 650) {
      return (
        <CloseIcon
          className={styles.clearSearchIcon}
          onClick={() => {
            setSearchInput('');
            handleListingSearch('');
          }}
        />
      );
    }

    if (search_input !== '' && windowWidth <= 650) {
      return (
        <Button
          onClick={() => {
            setSearchInput('');
            setSearchResults([]);
          }}
          variant='contained'
          className={styles.clearSearchButton}
        >
          {fmtClearSearch}
        </Button>
      );
    }

    return null;
  }

  function loadFilters() {
    return filters.map((filter) => {
      return (
        <Tooltip
          key={filter.name}
          title={
            <>
              <Typography variant='subtitle1'>
                {intl.formatMessage({ id: `portal.serviceOffer_${filter.name}` })}
              </Typography>
            </>
          }
        >
          <Box
            onClick={() => {
              const new_selected_filters = { ...selected_filters };
              new_selected_filters[filter.name] = !new_selected_filters[filter.name];
              setSelectedFilters(new_selected_filters);
            }}
            className={
              selected_filters[filter.name] ? styles.selectedFilterButton : styles.filterButton
            }
            style={filter.name === 'matterport' ? { borderRight: 'none' } : null}
          >
            <img
              className={styles.filterButtonIcon}
              src={selected_filters[filter.name] ? filter.image_src_selected : filter.image_src}
              alt='shooting'
            />
          </Box>
        </Tooltip>
      );
    });
  }

  return (
    <Box className={styles.searchBar}>
      <InputBase
        className={styles.searchInput}
        onChange={(e) => {
          setSearchInput(e.target.value);
          handleListingSearch(e.target.value);
        }}
        placeholder={fmtSearchListing}
        value={search_input}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleListingSearch(search_input);
          }
        }}
      />
      {loadClearSearchButton()}
      <Box className={styles.filterContainer}>{loadFilters()}</Box>
      <Button
        variant='contained'
        onClick={() => {
          handleListingSearch(search_input);
        }}
        className={styles.searchButton}
        style={{ backgroundColor: secondary_color }}
      >
        <SearchIcon />
      </Button>
    </Box>
  );
}

export default SearchBar;
